import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { countries } from '../../../data/countries'; // Import countries data
import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';
// ✅ Import the addSupplier service
import { addSupplier } from '../services/supplierService';
import { useTranslation } from "react-i18next";

const AddSupplierPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    taxNumber: '',
    bankAccount: '',
    paymentTerms: '',
    notes: '',          // ✅ Keep notes but will limit to 500 chars
    isActive: true
    // ✅ Removed website: ''
  });

  // Form validation errors
  const [errors, setErrors] = useState({});

  // Add state for user interaction tracking
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    
    // ✅ Enforce 500 character limit for notes field
    if (name === 'notes' && value.length > 500) {
      return; // Don't update if exceeding 500 characters
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name] && touchedFields[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Add handleFieldBlur function
  const handleFieldBlur = (event) => {
    const { name, value } = event.target;
    
    if (hasUserInteracted || hasAttemptedSubmit) {
      validateSingleField(name, value);
    }
  };

  // Add validateSingleField function
  const validateSingleField = (fieldName, value) => {
    if (!hasUserInteracted && !hasAttemptedSubmit) return;

    let error = '';
    
    switch (fieldName) {
      case 'name':
        if (!value.trim()) {
          error = 'Supplier name is required';
        } else if (value.length > 100) {
          error = 'Name cannot exceed 100 characters';
        }
        break;
      case 'contactPerson':
        if (!value.trim()) {
          error = 'Contact person is required';
        } else if (value.length > 100) {
          error = 'Contact person cannot exceed 100 characters';
        }
        break;
      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          error = 'Email is invalid';
        }
        break;
      case 'taxNumber':
        if (value && value.length > 50) {
          error = 'BTW Number cannot exceed 50 characters';
        }
        break;
      case 'address':
        if (value && value.length > 100) {
          error = 'Street cannot exceed 100 characters';
        }
        break;
      case 'postalCode':
        if (value && value.length > 20) {
          error = 'Post code cannot exceed 20 characters';
        }
        break;
      case 'city':
        if (value && value.length > 50) {
          error = 'City cannot exceed 50 characters';
        }
        break;
      case 'country':
        if (value && value.length > 50) {
          error = 'Country cannot exceed 50 characters';
        }
        break;
      case 'phone':
        if (value && value.length > 20) {
          error = 'Mobile number cannot exceed 20 characters';
        }
        break;
      case 'notes':
        if (value && value.length > 500) {
          error = 'Notes cannot exceed 500 characters';
        }
        break;
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  // Add shouldShowError function
  const shouldShowError = (fieldName) => {
    return (hasUserInteracted && touchedFields[fieldName]) || hasAttemptedSubmit;
  };

  // ✅ Update validation to match backend requirements
  const validateForm = () => {
    const newErrors = {};

    // ✅ Required fields per backend model
    if (!formData.name.trim()) {
      newErrors.name = 'Supplier name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name cannot exceed 100 characters';
    }

    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person is required';
    } else if (formData.contactPerson.length > 100) {
      newErrors.contactPerson = 'Contact person cannot exceed 100 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // ✅ Optional field validations based on backend model
    if (formData.taxNumber && formData.taxNumber.length > 50) {
      newErrors.taxNumber = 'BTW Number cannot exceed 50 characters';
    }

    if (formData.address && formData.address.length > 100) {
      newErrors.address = 'Street cannot exceed 100 characters';
    }

    if (formData.postalCode && formData.postalCode.length > 20) {
      newErrors.postalCode = 'Post code cannot exceed 20 characters';
    }

    if (formData.city && formData.city.length > 50) {
      newErrors.city = 'City cannot exceed 50 characters';
    }

    if (formData.country && formData.country.length > 50) {
      newErrors.country = 'Country cannot exceed 50 characters';
    }

    if (formData.phone && formData.phone.length > 20) {
      newErrors.phone = 'Mobile number cannot exceed 20 characters';
    }

    if (formData.notes && formData.notes.length > 500) {
      newErrors.notes = 'Notes cannot exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Handle form submission using supplier service
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    setHasAttemptedSubmit(true);
    
    if (!validateForm()) {
      toast.error('Please fix the form errors');
      return;
    }

    setLoading(true);

    try {
      // ✅ Map form data to backend model
      const supplierData = {
        Name: formData.name.trim(),
        Contactperson: formData.contactPerson.trim(),
        Email: formData.email.trim(),
        BTWNumber: formData.taxNumber.trim() || null,
        Street: formData.address.trim() || null,
        StreetNumber: null,
        PostCode: formData.postalCode.trim() || null,
        City: formData.city.trim() || null,
        Country: formData.country.trim() || null,
        MobileNumber: formData.phone.trim() || null,
        Notes: formData.notes.trim() || null
      };

      console.log('📤 Sending supplier data to API:', supplierData);

      // ✅ Call addSupplier service method
      const response = await addSupplier(supplierData);
      
      console.log('✅ Supplier added successfully:', response);
      toast.success('Supplier added successfully!');
      
      // ✅ Navigate back to suppliers list
      navigate('/suppliers', {
        state: { 
          message: 'Supplier added successfully',
          supplierId: response.Id || response.id
        }
      });

    } catch (error) {
      console.error('❌ Error adding supplier:', error);
      
      // ✅ Handle different error responses
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Handle validation errors from backend
        setErrors(error.response.data.errors);
        toast.error('Please fix the form errors');
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to add supplier. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/suppliers');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* ✅ Updated Header - smaller, more elegant */}
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h5" 
          component="h1" 
          sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            textAlign: 'left', 
            mb: 0.5 
          }}
        >
          {t("add_supplier")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("manage_suppliers_desc")}
        </Typography>
      </Box>

      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, // ✅ Reduced padding from 4 to 3
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <form onSubmit={handleSubmit} noValidate> {/* ✅ ADD noValidate HERE */}
          <Grid container spacing={2.5}> {/* ✅ Reduced spacing from 3 to 2.5 */}
            
            {/* ✅ Basic Information Section - Improved styling */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5, // ✅ Reduced margin
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem' // ✅ Slightly smaller section headers
                  }}
                >
                  {t("basic_information")}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("supplier_name") + " *"} // Indicate required field
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.name && shouldShowError('name'))}
                helperText={shouldShowError('name') ? errors.name : ' '}
                // required  // ✅ REMOVE THIS
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("contact_person")}
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.contactPerson && shouldShowError('contactPerson'))}
                helperText={shouldShowError('contactPerson') ? errors.contactPerson : ' '}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("email_address")}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.email && shouldShowError('email'))}
                helperText={shouldShowError('email') ? errors.email : ' '}
                // required  // ✅ REMOVE THIS
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("phone_number")}
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.phone && shouldShowError('phone'))}
                helperText={shouldShowError('phone') ? errors.phone : ' '}
                // required  // ✅ REMOVE THIS
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("vat_number")}
                name="taxNumber"
                value={formData.taxNumber}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.taxNumber && shouldShowError('taxNumber'))}
                helperText={shouldShowError('taxNumber') ? errors.taxNumber : ' '}
                disabled={loading}
                placeholder="BE0123456789"
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Removed Company Registration Number field as per requirements */}

            {/* ✅ Address Information Section - Improved styling */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5, // ✅ Reduced margin
                mt: 2, // ✅ Reduced top margin
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  {t("address_information")}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("address")}
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.address && shouldShowError('address'))}
                helperText={shouldShowError('address') ? errors.address : ' '}
                // required  // ✅ REMOVE THIS
                disabled={loading}
                multiline
                rows={2}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                  '& .MuiFormHelperText-root': {
                    display: 'flex',
                    justifyContent: 'space-between'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("city")}
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.city && shouldShowError('city'))}
                helperText={shouldShowError('city') ? errors.city : ' '}
                // required  // ✅ REMOVE THIS
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                  '& .MuiFormHelperText-root': {
                    display: 'flex',
                    justifyContent: 'space-between'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("postal_code")}
                name="postalCode"
                value={formData.postalCode}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.postalCode && shouldShowError('postalCode'))}
                helperText={shouldShowError('postalCode') ? errors.postalCode : ' '}
                // required  // ✅ REMOVE THIS
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                  '& .MuiFormHelperText-root': {
                    display: 'flex',
                    justifyContent: 'space-between'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl 
                fullWidth 
                error={!!errors.country} 
                disabled={loading}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              >
                <InputLabel>{t("country")}</InputLabel>
                <Select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  label={t("country") + " *"} // Indicate required field
                  // required  // ✅ REMOVE THIS
                >
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={country.name}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.country && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.country}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* ✅ Banking Information Section - Improved styling */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                mt: 2,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  {t("banking_information")}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label={t("bank_account")}
                name="bankAccount"
                value={formData.bankAccount}
                onChange={handleInputChange}
                error={!!errors.bankAccount}
                helperText={errors.bankAccount || t("iban_placeholder")}
                disabled={loading}
                placeholder="BE68 5390 0754 7034"
                inputProps={{ style: { textTransform: 'uppercase' } }}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("bic")}
                name="bic"
                value={formData.bic}
                onChange={handleInputChange}
                error={!!errors.bic}
                helperText={errors.bic || t("bic_placeholder")}
                disabled={loading}
                placeholder="KREDBEBB"
                inputProps={{ style: { textTransform: 'uppercase' } }}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Grid>

            {/* ✅ Additional Information Section - Improved styling */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                mt: 2,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  {t("additional_information")}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("notes")}
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.notes && shouldShowError('notes'))}
                helperText={
                  shouldShowError('notes') ? errors.notes : 
                  `${formData.notes.length}/500 ${t("characters")}` // ✅ Show character count
                }
                disabled={loading}
                multiline
                rows={4}
                size="medium"
                inputProps={{ 
                  maxLength: 500  // ✅ HTML maxLength as backup
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                  '& .MuiFormHelperText-root': {
                    display: 'flex',
                    justifyContent: 'space-between'
                  }
                }}
                placeholder={t("notes_placeholder")}
              />
            </Grid>

            {/* ✅ Action Buttons - Improved styling */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-end', 
                mt: 3, // ✅ Reduced from 4 to 3
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: 2
                  }}
                >
                  {loading ? t("creating") : t("create_supplier")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default AddSupplierPage;